import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Eye,
  Users,
  GraduationCap,
  Clock,
  MessageCircle,
  Star
} from 'lucide-react';

interface Skill {
  id: number;
  name: string;
  description: string;
  category: SkillCategory;
  createdAt: string;
  updatedAt: string;
  users?: Array<{
    user: User;
    relationType: 'TEACH' | 'LEARN';
    proficiencyLevel?: number;
    proficiencyDescription?: string;
  }>;
}

interface SkillCategory {
  id: number;
  name: string;
  description: string;
}

interface DashboardStats {
  userStats: {
    totalUsers: number;
    newUsersThisMonth: number;
  };
  skillStats: {
    totalSkills: number;
    totalUserSkills: number;
  };
  sessionStats: {
    pendingSessionRequests: number;
    scheduledSessions: number;
    completedSessions: number;
    canceledSessions: number;
  };
  chatStats: {
    totalChatMessages: number;
  };
  feedbackStats: {
    totalFeedback: number;
  };
}

interface User {
  id: number;
  username: string;
  email: string;
  fullName?: string;
  bio?: string;
  profilePhotoUrl?: string;
  credits: number;
  enabled: boolean;
  blocked: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  roles: Array<{ id: number; name: string }>;
}

interface LearningSession {
  id: number;
  teacher: User;
  learner: User;
  skill: Skill;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED' | 'NO_SHOW';
  meetingNotes?: string;
  creditProcessed: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SessionRequest {
  id: number;
  requester: User;
  recipient: User;
  skill: Skill;
  message: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCELED';
  createdAt: string;
  updatedAt: string;
}

interface Feedback {
  id: number;
  fromUser: User;
  toUser: User;
  session: LearningSession;
  rating: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
}

// Unused interfaces removed

const Admin: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'skills' | 'categories' | 'users' | 'sessions' | 'feedback'>('dashboard');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Session management state
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  const [sessionRequests, setSessionRequests] = useState<SessionRequest[]>([]);
  const [sessionStats, setSessionStats] = useState<any>(null);
  const [selectedSession, setSelectedSession] = useState<LearningSession | null>(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  
  // Search and filter states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userCreditRange, setUserCreditRange] = useState({ min: '', max: '' });
  const [userDateRange, setUserDateRange] = useState({ start: '', end: '' });
  
  // Form states
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [editingCategory, setEditingCategory] = useState<SkillCategory | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form data
  const [skillForm, setSkillForm] = useState({
    name: '',
    description: '',
    categoryId: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });

  const [creditForm, setCreditForm] = useState({
    amount: 0
  });

  // Feedback management state
  const [feedbackList, setFeedbackList] = useState<any[]>([]);
  
  // Matchmaking management state removed

  useEffect(() => {
    if (isAuthenticated && user?.roles?.some(role => role.name === 'ROLE_ADMIN')) {
      loadData();
    }
  }, [isAuthenticated, user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load basic admin data first
      const [skillsRes, categoriesRes, dashboardRes, usersRes] = await Promise.all([
        apiService.getAllSkills(),
        apiService.getAllSkillCategories(),
        apiService.getAdminDashboard(),
        apiService.getAdminUsers()
      ]);
      
      setSkills(skillsRes.data || []);
      setCategories(categoriesRes.data || []);
      setDashboardStats(dashboardRes.data || null);
      setUsers(usersRes.data || []);
      
      // Try to load session data, but don't fail if endpoints don't exist yet
      try {
        const [sessionsRes, sessionRequestsRes, sessionStatsRes, feedbackRes] = await Promise.all([
          apiService.getAdminAllSessions(),
          apiService.getAdminAllSessionRequests(),
          apiService.getAdminSessionStats(),
          apiService.getAdminAllFeedback()
        ]);
        
        setSessions(sessionsRes.data || []);
        setSessionRequests(sessionRequestsRes.data || []);
        setSessionStats(sessionStatsRes.data || null);
        setFeedbackList(feedbackRes.data || []);
      } catch (sessionError) {
        console.warn('Session/Feedback endpoints not available yet:', sessionError);
        // Set empty data for sessions and feedback
        setSessions([]);
        setSessionRequests([]);
        setSessionStats(null);
        setFeedbackList([]);
      }
      
    } catch (error: any) {
      console.error('Admin data load error:', error);
      toast.error('Failed to load admin data. Please try again.');
      
      // Set empty arrays as fallback
      setSkills([]);
      setCategories([]);
      setDashboardStats(null);
      setUsers([]);
      setSessions([]);
      setSessionRequests([]);
      setSessionStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!skillForm.name.trim() || !skillForm.categoryId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingSkill) {
        await apiService.updateSkill(editingSkill.id, {
          name: skillForm.name,
          description: skillForm.description,
          categoryId: parseInt(skillForm.categoryId)
        });
        toast.success('Skill updated successfully!');
      } else {
        await apiService.createSkill({
          name: skillForm.name,
          description: skillForm.description,
          categoryId: parseInt(skillForm.categoryId)
        });
        toast.success('Skill created successfully!');
      }
      
      setShowSkillModal(false);
      setEditingSkill(null);
      setSkillForm({ name: '', description: '', categoryId: '' });
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save skill';
      toast.error(errorMessage);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryForm.name.trim()) {
      toast.error('Please fill in the category name');
      return;
    }

    try {
      if (editingCategory) {
        await apiService.updateSkillCategory(editingCategory.id, {
          name: categoryForm.name,
          description: categoryForm.description
        });
        toast.success('Category updated successfully!');
      } else {
        await apiService.createSkillCategory({
          name: categoryForm.name,
          description: categoryForm.description
        });
        toast.success('Category created successfully!');
      }
      
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save category';
      toast.error(errorMessage);
    }
  };

  const handleDeleteSkill = async (skillId: number) => {
    if (!window.confirm('Are you sure you want to delete this skill? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteSkill(skillId);
      toast.success('Skill deleted successfully!');
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete skill';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all skills in this category.')) {
      return;
    }

    try {
      await apiService.deleteSkillCategory(categoryId);
      toast.success('Category deleted successfully!');
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete category';
      toast.error(errorMessage);
    }
  };

  // User management functions
  const handleBlockUser = async (userId: number, blocked: boolean) => {
    try {
      await apiService.blockUser(userId, blocked);
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, blocked } : user
      ));
      toast.success(`User ${blocked ? 'blocked' : 'unblocked'} successfully!`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user status';
      toast.error(errorMessage);
    }
  };

  const handleAdjustCredits = async (userId: number, amount: number) => {
    try {
      await apiService.adjustUserCredits(userId, amount);
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, credits: user.credits + amount } : user
      ));
      toast.success(`User credits adjusted by ${amount > 0 ? '+' : ''}${amount}!`);
      setShowCreditModal(false);
      setSelectedUser(null);
      setCreditForm({ amount: 0 });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to adjust user credits';
      toast.error(errorMessage);
    }
  };

  const openCreditModal = (user: User) => {
    setSelectedUser(user);
    setCreditForm({ amount: 0 });
    setShowCreditModal(true);
  };

  const handleCreditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser && creditForm.amount !== 0) {
      handleAdjustCredits(selectedUser.id, creditForm.amount);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
      toast.success('User deleted successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (user: User) => {
    if (user.deleted) return 'bg-gray-100 text-gray-800';
    if (user.blocked) return 'bg-red-100 text-red-800';
    if (!user.enabled) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (user: User) => {
    if (user.deleted) return 'Deleted';
    if (user.blocked) return 'Blocked';
    if (!user.enabled) return 'Disabled';
    return 'Active';
  };

  // Filter users based on search and filter criteria
  const getFilteredUsers = () => {
    return users.filter(user => {
      // Search term filter
      if (userSearchTerm && !(
        user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.fullName && user.fullName.toLowerCase().includes(userSearchTerm.toLowerCase()))
      )) {
        return false;
      }

      // Status filter
      if (userStatusFilter) {
        const userStatus = getStatusText(user).toLowerCase();
        if (userStatus !== userStatusFilter.toLowerCase()) {
          return false;
        }
      }

      // Role filter
      if (userRoleFilter) {
        const hasRole = user.roles.some(role => role.name === userRoleFilter);
        if (!hasRole) {
          return false;
        }
      }

      // Credit range filter
      if (userCreditRange.min && user.credits < parseInt(userCreditRange.min)) {
        return false;
      }
      if (userCreditRange.max && user.credits > parseInt(userCreditRange.max)) {
        return false;
      }

      // Date range filter
      if (userDateRange.start) {
        const userDate = new Date(user.createdAt);
        const startDate = new Date(userDateRange.start);
        if (userDate < startDate) {
          return false;
        }
      }
      if (userDateRange.end) {
        const userDate = new Date(user.createdAt);
        const endDate = new Date(userDateRange.end);
        if (userDate > endDate) {
          return false;
        }
      }

      return true;
    });
  };

  const clearUserFilters = () => {
    setUserSearchTerm('');
    setUserStatusFilter('');
    setUserRoleFilter('');
    setUserCreditRange({ min: '', max: '' });
    setUserDateRange({ start: '', end: '' });
  };

  const openSkillModal = (skill?: Skill) => {
    if (skill) {
      setEditingSkill(skill);
      setSkillForm({
        name: skill.name,
        description: skill.description,
        categoryId: skill.category.id.toString()
      });
    } else {
      setEditingSkill(null);
      setSkillForm({ name: '', description: '', categoryId: '' });
    }
    setShowSkillModal(true);
  };

  const openCategoryModal = (category?: SkillCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
    }
    setShowCategoryModal(true);
  };

  // Session management functions
  const handleProcessSessionCredits = async (sessionId: number) => {
    try {
      await apiService.processSessionCredits(sessionId);
      toast.success('Session credits processed successfully!');
      loadData(); // Reload data to update creditProcessed status
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to process session credits';
      toast.error(errorMessage);
    }
  };

  const openSessionModal = (session: LearningSession) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const handleAcceptSessionRequest = async (requestId: number) => {
    try {
      await apiService.acceptSessionRequest(requestId);
      toast.success('Session request accepted successfully!');
      loadData(); // Reload data to update status
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to accept session request';
      toast.error(errorMessage);
    }
  };

  const handleRejectSessionRequest = async (requestId: number) => {
    try {
      await apiService.rejectSessionRequest(requestId);
      toast.success('Session request rejected successfully!');
      loadData(); // Reload data to update status
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reject session request';
      toast.error(errorMessage);
    }
  };

  // Feedback management functions
  const handleDeleteFeedback = async (feedbackId: number) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      await apiService.deleteFeedback(feedbackId);
      setFeedbackList(prev => prev.filter(feedback => feedback.id !== feedbackId));
      toast.success('Feedback deleted successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete feedback';
      toast.error(errorMessage);
    }
  };

  // Check if user is admin
  if (!isAuthenticated || !user?.roles?.some(role => role.name === 'ROLE_ADMIN')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-20 w-20 bg-gradient-to-r from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Users className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold gradient-text mb-4">Access Denied</h1>
          <p className="text-gray-600 text-lg">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Admin Data...</h2>
          <p className="text-gray-600">Preparing administrative interface</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text">Admin Dashboard</h1>
          <p className="text-gray-600 mt-3 text-lg">Manage skills, categories, and view system statistics</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-blue-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('skills')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'skills'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Skills Management
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Categories Management
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'sessions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Session Management
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'feedback'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Feedback Management
            </button>
          </nav>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {!dashboardStats ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading dashboard data...</p>
              </div>
            ) : (
              <>
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Users</p>
                        <p className="text-2xl font-semibold text-gray-900">{dashboardStats.userStats?.totalUsers || 0}</p>
                        <p className="text-xs text-green-600">+{dashboardStats.userStats?.newUsersThisMonth || 0} this month</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                    <div className="flex items-center">
                      <GraduationCap className="h-8 w-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Skills</p>
                        <p className="text-2xl font-semibold text-gray-900">{dashboardStats.skillStats?.totalSkills || 0}</p>
                        <p className="text-xs text-blue-600">Skills available</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
                    <div className="flex items-center">
                      <Clock className="h-8 w-8 text-yellow-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                        <p className="text-2xl font-semibold text-gray-900">{dashboardStats.sessionStats?.pendingSessionRequests || 0}</p>
                        <p className="text-xs text-orange-600">Requires attention</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                    <div className="flex items-center">
                      <MessageCircle className="h-8 w-8 text-purple-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Chat Messages</p>
                        <p className="text-2xl font-semibold text-gray-900">{dashboardStats.chatStats?.totalChatMessages || 0}</p>
                        <p className="text-xs text-purple-600">Total messages</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Statistics Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* User Statistics */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Users className="h-5 w-5 text-blue-600 mr-2" />
                      User Statistics
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Users</span>
                        <span className="font-medium text-green-600">{dashboardStats.userStats?.totalUsers || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">New Users This Month</span>
                        <span className="font-medium text-blue-600">{dashboardStats.userStats?.newUsersThisMonth || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total User Skills</span>
                        <span className="font-medium text-purple-600">{dashboardStats.skillStats?.totalUserSkills || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Session Statistics */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                      Session Statistics
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Scheduled Sessions</span>
                        <span className="font-medium text-blue-600">{dashboardStats.sessionStats?.scheduledSessions || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Completed Sessions</span>
                        <span className="font-medium text-green-600">{dashboardStats.sessionStats?.completedSessions || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Canceled Sessions</span>
                        <span className="font-medium text-red-600">{dashboardStats.sessionStats?.canceledSessions || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills Analytics */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <GraduationCap className="h-5 w-5 text-green-600 mr-2" />
                    Skills Analytics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map((category) => {
                      const skillCount = skills.filter(skill => skill.category.id === category.id).length;
                      return (
                        <div key={category.id} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">{category.name}</h4>
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Skills:</span>
                              <span className="font-medium">{skillCount}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Category ID:</span>
                              <span className="font-medium">{category.id}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Platform Insights */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Star className="h-5 w-5 text-yellow-600 mr-2" />
                    Platform Insights
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Current Status</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Skills:</span>
                          <span className="font-medium text-blue-600">{dashboardStats?.skillStats?.totalSkills || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Categories:</span>
                          <span className="font-medium text-green-600">{categories.length}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Users:</span>
                          <span className="font-medium text-purple-600">{dashboardStats?.userStats?.totalUsers || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Skills Distribution</h4>
                      <div className="space-y-2">
                        {categories.map((category) => {
                          const skillCount = skills.filter(skill => skill.category.id === category.id).length;
                          const percentage = skills.length > 0 ? ((skillCount / skills.length) * 100).toFixed(1) : '0';
                          return (
                            <div key={category.id} className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm">{category.name}</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium">{skillCount}</span>
                                <span className="text-xs text-gray-500">({percentage}%)</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Skills Management Tab */}
        {activeTab === 'skills' && (
          <div className="space-y-6">
            {/* Skills Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Skills Overview</h2>
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    Total Skills: <span className="font-semibold text-blue-600">{skills.length}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Categories: <span className="font-semibold text-green-600">{categories.length}</span>
                  </div>
                </div>
              </div>
              
              {/* Search and Filter */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search skills by name..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => openSkillModal()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Skill
                </button>
              </div>
            </div>

            {/* Skills Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {skills.map((skill) => (
                      <tr key={skill.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{skill.name}</div>
                            <div className="text-xs text-gray-500">ID: {skill.id}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={skill.description}>
                            {skill.description || 'No description'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {skill.category.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {/* This would need backend support to show actual user count */}
                          <span className="text-gray-400">-</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(skill.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openSkillModal(skill)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Edit skill"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSkill(skill.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete skill"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {skills.length === 0 && (
                <div className="text-center py-12">
                  <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No skills found</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating your first skill.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => openSkillModal()}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Skill
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories Management Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            {/* Categories Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Categories Overview</h2>
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    Total Categories: <span className="font-semibold text-blue-600">{categories.length}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Skills: <span className="font-semibold text-green-600">{skills.length}</span>
                  </div>
                </div>
              </div>
              
              {/* Category Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {categories.map((category) => {
                  const skillCount = skills.filter(skill => skill.category.id === category.id).length;
                  return (
                    <div key={category.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">{category.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {skillCount} skills
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => openCategoryModal()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </button>
              </div>
            </div>

            {/* Categories Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skills Count</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories.map((category) => {
                      const skillCount = skills.filter(skill => skill.category.id === category.id).length;
                      return (
                        <tr key={category.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{category.name}</div>
                              <div className="text-xs text-gray-500">ID: {category.id}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate" title={category.description}>
                              {category.description || 'No description'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900">{skillCount}</span>
                              {skillCount === 0 && (
                                <span className="ml-2 text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                  Empty
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openCategoryModal(category)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="Edit category"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Delete category"
                                disabled={skillCount > 0}
                              >
                                <Trash2 className={`h-4 w-4 ${skillCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {categories.length === 0 && (
                <div className="text-center py-12">
                  <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No categories found</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating your first category.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => openCategoryModal()}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Users Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                <div className="text-sm text-gray-600">
                  Total Users: <span className="font-semibold text-blue-600">{users.length}</span>
                  {getFilteredUsers().length !== users.length && (
                    <span className="ml-2 text-orange-600">
                      (Filtered: {getFilteredUsers().length})
                    </span>
                  )}
                </div>
              </div>
              
              {/* Advanced Search and Filter */}
              <div className="space-y-4 mb-6">
                {/* Search Bar */}
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search users by username, email, or full name..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Filter Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <select 
                    value={userStatusFilter}
                    onChange={(e) => setUserStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                    <option value="disabled">Disabled</option>
                    <option value="deleted">Deleted</option>
                  </select>
                  
                  <select 
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Roles</option>
                    <option value="ROLE_USER">Regular Users</option>
                    <option value="ROLE_ADMIN">Admins</option>
                  </select>
                  
                  <input
                    type="number"
                    placeholder="Min Credits"
                    value={userCreditRange.min}
                    onChange={(e) => setUserCreditRange({ ...userCreditRange, min: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="number"
                    placeholder="Max Credits"
                    value={userCreditRange.max}
                    onChange={(e) => setUserCreditRange({ ...userCreditRange, max: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Filter Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="date"
                    placeholder="Start Date"
                    value={userDateRange.start}
                    onChange={(e) => setUserDateRange({ ...userDateRange, start: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <input
                    type="date"
                    placeholder="End Date"
                    value={userDateRange.end}
                    onChange={(e) => setUserDateRange({ ...userDateRange, end: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  <button
                    onClick={clearUserFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
 
             {/* User Statistics */}
             <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
               <div className="bg-white rounded-lg shadow p-6">
                 <div className="flex items-center">
                   <div className="flex-shrink-0">
                     <Users className="h-8 w-8 text-blue-600" />
                   </div>
                   <div className="ml-4">
                     <p className="text-sm font-medium text-gray-500">Total Users</p>
                     <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
                   </div>
                 </div>
               </div>
               
               <div className="bg-white rounded-lg shadow p-6">
                 <div className="flex items-center">
                   <div className="flex-shrink-0">
                     <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                       <div className="h-4 w-4 bg-green-600 rounded-full"></div>
                     </div>
                   </div>
                   <div className="ml-4">
                     <p className="text-sm font-medium text-gray-500">Active Users</p>
                     <p className="text-2xl font-semibold text-gray-900">
                       {users.filter(u => !u.deleted && !u.blocked && u.enabled).length}
                     </p>
                   </div>
                 </div>
               </div>
               
               <div className="bg-white rounded-lg shadow p-6">
                 <div className="flex items-center">
                   <div className="flex-shrink-0">
                     <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                       <div className="h-4 w-4 bg-red-600 rounded-full"></div>
                     </div>
                   </div>
                   <div className="ml-4">
                     <p className="text-sm font-medium text-gray-500">Blocked Users</p>
                     <p className="text-2xl font-semibold text-gray-900">
                       {users.filter(u => u.blocked).length}
                     </p>
                   </div>
                 </div>
               </div>
               
               <div className="bg-white rounded-lg shadow p-6">
                 <div className="flex items-center">
                   <div className="flex-shrink-0">
                     <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                       <div className="h-4 w-4 bg-yellow-600 rounded-full"></div>
                     </div>
                   </div>
                   <div className="ml-4">
                     <p className="text-sm font-medium text-gray-500">New This Month</p>
                     <p className="text-2xl font-semibold text-gray-900">
                       {users.filter(u => {
                         const userDate = new Date(u.createdAt);
                         const monthStart = new Date();
                         monthStart.setDate(1);
                         monthStart.setHours(0, 0, 0, 0);
                         return userDate >= monthStart;
                       }).length}
                     </p>
                   </div>
                 </div>
               </div>
             </div>
 
             {/* Users Table */}
            <div className="bg-white rounded-lg shadow">
              {/* Bulk Actions */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-700">
                      {getFilteredUsers().length} users selected
                    </span>
                    <button
                      onClick={() => {
                        const activeUsers = getFilteredUsers().filter(u => !u.blocked && !u.deleted);
                        if (activeUsers.length > 0) {
                          if (window.confirm(`Block ${activeUsers.length} active users?`)) {
                            activeUsers.forEach(user => handleBlockUser(user.id, true));
                          }
                        }
                      }}
                      className="px-3 py-1 text-sm text-red-700 bg-red-100 border border-red-300 rounded hover:bg-red-200"
                      disabled={getFilteredUsers().filter(u => !u.blocked && !u.deleted).length === 0}
                    >
                      Block Selected
                    </button>
                    <button
                      onClick={() => {
                        const blockedUsers = getFilteredUsers().filter(u => u.blocked);
                        if (blockedUsers.length > 0) {
                          if (window.confirm(`Unblock ${blockedUsers.length} blocked users?`)) {
                            blockedUsers.forEach(user => handleBlockUser(user.id, false));
                          }
                        }
                      }}
                      className="px-3 py-1 text-sm text-green-700 bg-green-100 border border-green-300 rounded hover:bg-green-200"
                      disabled={getFilteredUsers().filter(u => u.blocked).length === 0}
                    >
                      Unblock Selected
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    Showing {getFilteredUsers().length} of {users.length} users
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredUsers().map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          <div className="text-xs text-gray-500">ID: {user.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.fullName || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.credits}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user)}`}>
                            {getStatusText(user)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleBlockUser(user.id, !user.blocked)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title={user.blocked ? 'Unblock User' : 'Block User'}
                            >
                              <Eye className={`h-4 w-4 ${user.blocked ? 'text-green-600' : 'text-red-600'}`} />
                            </button>
                            <button
                              onClick={() => openCreditModal(user)}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="Add Credits"
                            >
                              <GraduationCap className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {getFilteredUsers().length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="mt-1 text-sm text-gray-500">There are no users registered yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Session Management Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            {/* Session Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Session Management</h2>
                <div className="text-sm text-gray-600">
                  Total Sessions: <span className="font-semibold text-blue-600">{sessions.length}</span>
                </div>
              </div>
              
              {/* Warning message if session data is not available */}
              {sessions.length === 0 && sessionStats === null && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-5 w-5 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="text-yellow-800 text-xs font-bold">!</span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Session Management Setup Required
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Session management endpoints are not available yet. This could be because:
                        </p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>The backend needs to be restarted to pick up new admin endpoints</li>
                          <li>Database tables for sessions are not created yet</li>
                          <li>Session management is not fully implemented in the backend</li>
                        </ul>
                        <p className="mt-2">
                          <strong>Current Status:</strong> Basic admin functions (Users, Skills, Categories) are working.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Session Statistics */}
            {sessionStats && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Clock className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Total Sessions</p>
                      <p className="text-2xl font-semibold text-gray-900">{sessionStats.totalSessions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-green-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Completed</p>
                      <p className="text-2xl font-semibold text-gray-900">{sessionStats.completedSessions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-yellow-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Scheduled</p>
                      <p className="text-2xl font-semibold text-gray-900">{sessionStats.scheduledSessions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-red-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Canceled</p>
                      <p className="text-2xl font-semibold text-gray-900">{sessionStats.canceledSessions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-orange-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">No Show</p>
                      <p className="text-2xl font-semibold text-gray-900">{sessionStats.noShowSessions || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sessions Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">All Learning Sessions</h3>
                  <div className="text-sm text-gray-500">
                    Showing {sessions.length} sessions
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Learner</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">#{session.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{session.teacher.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{session.learner.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{session.skill.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            session.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            session.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                            session.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
                            session.status === 'NO_SHOW' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {session.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(session.scheduledStartTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {session.status === 'COMPLETED' && !session.creditProcessed && (
                              <button
                                onClick={() => handleProcessSessionCredits(session.id)}
                                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                title="Process Credits"
                              >
                                <GraduationCap className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openSessionModal(session)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {sessions.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
                  <p className="mt-1 text-sm text-gray-500">There are no learning sessions yet.</p>
                </div>
              )}
            </div>

            {/* Session Requests */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Session Requests</h3>
                  <div className="text-sm text-gray-500">
                    {sessionRequests.filter(r => r.status === 'PENDING').length} pending requests
                  </div>
                </div>
              </div>
              
              {/* Warning message if session requests are not available */}
              {sessionRequests.length === 0 && (
                <div className="px-6 py-4 text-center">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No session requests found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {sessions.length === 0 ? 
                      'Session management is not available yet. Please check the setup requirements above.' : 
                      'There are no session requests yet.'
                    }
                  </p>
                </div>
              )}
              
              {sessionRequests.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requester</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sessionRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">#{request.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.requester.username}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.recipient.username}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.skill.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                              request.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {request.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(request.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {request.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={() => handleAcceptSessionRequest(request.id)}
                                    className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                    title="Accept Request"
                                  >
                                    <div className="h-4 w-4 bg-green-600 rounded-full"></div>
                                  </button>
                                  <button
                                    onClick={() => handleRejectSessionRequest(request.id)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                    title="Reject Request"
                                  >
                                    <div className="h-4 w-4 bg-red-600 rounded-full"></div>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feedback Management Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Feedback Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Feedback Management</h2>
                <div className="text-sm text-gray-600">
                  Total Feedback: <span className="font-semibold text-blue-600">{feedbackList.length}</span>
                </div>
              </div>
            </div>

            {/* Feedback Statistics */}
            {feedbackList.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-blue-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Average Rating</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {(feedbackList.reduce((sum, f) => sum + f.rating, 0) / feedbackList.length).toFixed(1)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-green-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">5-Star Ratings</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {feedbackList.filter(f => f.rating === 5).length}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-yellow-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">This Month</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {feedbackList.filter(f => {
                          const feedbackDate = new Date(f.createdAt);
                          const monthStart = new Date();
                          monthStart.setDate(1);
                          monthStart.setHours(0, 0, 0, 0);
                          return feedbackDate >= monthStart;
                        }).length}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                        <div className="h-4 w-4 bg-red-600 rounded-full"></div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Low Ratings (1-2)</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {feedbackList.filter(f => f.rating <= 2).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">All Feedback</h3>
                  <div className="text-sm text-gray-500">
                    Showing {feedbackList.length} feedback entries
                  </div>
                </div>
              </div>
              
              {feedbackList.length === 0 ? (
                <div className="px-6 py-4 text-center">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No feedback found</h3>
                  <p className="mt-1 text-sm text-gray-500">There is no feedback in the system yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {feedbackList.map((feedback) => (
                        <tr key={feedback.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{feedback.fromUser.username}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{feedback.toUser.username}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">#{feedback.session.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                feedback.rating >= 4 ? 'bg-green-100 text-green-800' :
                                feedback.rating >= 3 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {feedback.rating} ⭐
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate" title={feedback.comment}>
                              {feedback.comment}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(feedback.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleDeleteFeedback(feedback.id)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Delete Feedback"
                                >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Skill Modal */}
      {showSkillModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSkill ? 'Edit Skill' : 'Add New Skill'}
              </h3>
              <form onSubmit={handleSkillSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Skill Name *</label>
                  <input
                    type="text"
                    value={skillForm.name}
                    onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={skillForm.description}
                    onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={skillForm.categoryId}
                    onChange={(e) => setSkillForm({ ...skillForm, categoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowSkillModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {editingSkill ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <form onSubmit={handleCategorySubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {showCreditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Adjust Credits for {selectedUser.username}
              </h3>
              <form onSubmit={handleCreditSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <input
                    type="number"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm({ ...creditForm, amount: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Adjust Credits
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Session Modal */}
      {showSessionModal && selectedSession && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Session Details #{selectedSession.id}
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                  <p className="text-sm text-gray-900">{selectedSession.teacher.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Learner</label>
                  <p className="text-sm text-gray-900">{selectedSession.learner.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
                  <p className="text-sm text-gray-900">{selectedSession.skill.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedSession.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                    selectedSession.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                    selectedSession.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
                    selectedSession.status === 'NO_SHOW' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedSession.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Start</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedSession.scheduledStartTime)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled End</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedSession.scheduledEndTime)}</p>
                </div>
              </div>
              
              {selectedSession.meetingNotes && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Notes</label>
                  <p className="text-sm text-gray-900">{selectedSession.meetingNotes}</p>
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSessionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 